// eslint-disable-next-line no-restricted-imports -- diese Datei IST die Hülle
import MuiDialogTitle, { type DialogTitleProps } from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import BugReportIcon from '@mui/icons-material/BugReport';
import type { SxProps, Theme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useBugReport, type BugReportContextValue } from '../bug-report/BugReportProvider';

/**
 * Titelzeile für ALLE Popups der Anwendung.
 *
 * BEFUND: Der Melde-Knopf saß nur in der Kopfzeile. Ist ein Popup offen, liegt
 * der Grauschleier darüber — der Knopf war nicht klickbar. Genau in Popups
 * treten aber viele Fehler auf.
 *
 * LÖSUNG: Diese Hülle verhält sich wie das MUI-`DialogTitle` (alle
 * Eigenschaften werden durchgereicht) und zeigt zusätzlich den Melde-Knopf am
 * rechten Rand. Sie ersetzt in allen Popup-Dateien das MUI-`DialogTitle`; eine
 * ESLint-Regel sperrt den direkten MUI-Import.
 *
 * WARUM ABSOLUT POSITIONIERT statt als Flex-Kind: die meisten Titelzeilen sind
 * schlichte `<DialogTitle>`-Blöcke, einige setzen ein eigenes
 * `display:flex; justify-content:space-between` mit einem Schließen-Knopf als
 * letztem Kind. Ein zusätzliches Flex-Kind würde deren Aufteilung verschieben.
 * Der reservierte rechte Rand (`pr: 6`) plus ein absolut gesetzter Knopf lässt
 * beide Bauformen unverändert und setzt den Knopf verlässlich RECHTS NEBEN
 * den Schließen-Knopf.
 */

/** Rechter Rand, den der Melde-Knopf belegt (48px = MUI-Abstandsschritt 6). */
const MELDE_KNOPF_BREITE = 6;

function DialogBugButton({ bug }: { bug: BugReportContextValue }) {
  const { t } = useTranslation();
  return (
    <Tooltip title={t('bugReport.reportFromDialog', 'Fehler in diesem Fenster melden')}>
      {/* span, weil ein deaktivierter IconButton keine Zeigerereignisse
          liefert und der Tooltip sonst stumm bliebe. */}
      <span
        style={{
          position: 'absolute',
          right: 8,
          top: '50%',
          transform: 'translateY(-50%)',
          lineHeight: 0,
        }}
      >
        <IconButton
          size="small"
          onClick={bug.starteMeldung}
          disabled={bug.aufnahmeLaeuft}
          aria-busy={bug.aufnahmeLaeuft}
          // Gleiches Merkmal wie in der Kopfzeile: der Melde-Knopf selbst wird
          // vom Bildschirmabzug ausgenommen (siehe captureFullPage.ts).
          data-bug-report-button=""
          aria-label="bug-report"
        >
          <BugReportIcon fontSize="small" />
        </IconButton>
      </span>
    </Tooltip>
  );
}

export function AppDialogTitle({ children, sx, ...rest }: DialogTitleProps) {
  const bug = useBugReport();

  // Ohne Anbieter (Tests, Teilbäume ohne Anwendungswurzel) bleibt die
  // Titelzeile bitgenau das, was sie vorher war.
  if (!bug) {
    return (
      <MuiDialogTitle sx={sx} {...rest}>
        {children}
      </MuiDialogTitle>
    );
  }

  const eigeneStile: SxProps<Theme> = { position: 'relative', pr: MELDE_KNOPF_BREITE };
  // Array-Form, damit auch Funktions- und Array-`sx` der Aufrufer erhalten
  // bleiben; die Angaben des Aufrufers stehen hinten und gewinnen damit.
  const zusammengeführt: SxProps<Theme> = [
    eigeneStile,
    ...(Array.isArray(sx) ? sx : [sx]),
  ] as SxProps<Theme>;

  return (
    <MuiDialogTitle sx={zusammengeführt} {...rest}>
      {children}
      <DialogBugButton bug={bug} />
    </MuiDialogTitle>
  );
}

export default AppDialogTitle;
